import { useMemo } from 'react';
import { costFormater, useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { api } from '@/api';
import { SHORT_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';

import { PlanDetailRowData, UNLIMITED_KEY } from '../EmployerPlanDetails.types';
import {
  formatDaysUntilIndicator,
  getFundingMethodLabel,
  getNearestDeadlineKeys,
} from '../EmployerPlanDetails.utils';

const formatDate = (date?: Date) => (date ? dayjs(date).format(SHORT_DATE_FORMAT) : '');

const formatMonetaryValue = (value?: string) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return value;
  }
  return costFormater(numericValue);
};

const useGetNotionalPlanDetailsQuery = (planId: string) => {
  const { isLoading, data, isError, refetch, isSuccess } = useQuery(
    [API_URL.ACCOUNT_USAGE_PLANS_PLAN_DETAILS_NOTIONAL_BY_PLAN_ID(''), planId],
    () => api.get(API_URL.ACCOUNT_USAGE_PLANS_PLAN_DETAILS_NOTIONAL_BY_PLAN_ID(planId)),
    {
      retry: 0,
      enabled: !!planId,
    }
  );

  const planDetails = useMemo(() => data?.data, [data?.data]);
  const nearestDeadlineKeys = useMemo(
    () =>
      getNearestDeadlineKeys([
        { key: 'runoutDate', daysUntil: planDetails?.runout_days_until },
        { key: 'graceDate', daysUntil: planDetails?.grace_days_until },
        { key: 'rolloverDate', daysUntil: planDetails?.rollover_days_until },
        { key: 'forfeitureDate', daysUntil: planDetails?.forfeiture_days_until },
      ]),
    [
      planDetails?.runout_days_until,
      planDetails?.grace_days_until,
      planDetails?.rollover_days_until,
      planDetails?.forfeiture_days_until,
    ]
  );

  const minMaxLabel = useMemo(() => {
    if (!planDetails) {
      return '';
    }
    const minAmount = planDetails.rollover_min_amount;
    const maxAmount = planDetails.rollover_max_amount;
    const isUnlimited = minAmount === null && maxAmount === null;
    if (isUnlimited) {
      return 'Unlimited';
    }
    if (!!minAmount && !!maxAmount) {
      return `${formatMonetaryValue(minAmount)} / ${formatMonetaryValue(maxAmount)}`;
    }
    if (!!minAmount && !maxAmount) {
      return formatMonetaryValue(minAmount);
    }
    if (!minAmount && !!maxAmount) {
      return formatMonetaryValue(maxAmount);
    }
    return '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planDetails?.rollover_max_amount, planDetails?.rollover_min_amount]);
  const dataFormatter = useMemo<PlanDetailRowData[]>(() => {
    if (!planDetails) {
      return [];
    }

    const rows: PlanDetailRowData[] = [
      {
        key: 'planYear',
        label: 'Plan year',
        value: `${formatDate(planDetails.plan_year_valid_from)} - ${
          planDetails.plan_year_valid_to ? formatDate(planDetails.plan_year_valid_to) : 'Ongoing'
        }`,
      },
      ...(planDetails.runout_date
        ? [
            {
              key: 'runoutDate',
              label: 'Runout date',
              value: formatDate(planDetails.runout_date),
              indicator: nearestDeadlineKeys.has('runoutDate')
                ? formatDaysUntilIndicator(planDetails.runout_days_until)
                : undefined,
            },
          ]
        : []),
      ...(planDetails.is_carded === true || planDetails.is_carded === false
        ? [{ key: 'carded', label: 'Carded', value: planDetails.is_carded ? 'Yes' : 'No' }]
        : []),
      ...(planDetails.grace_date
        ? [
            {
              key: 'graceDate',
              label: 'Grace date',
              value: formatDate(planDetails.grace_date),
              indicator: nearestDeadlineKeys.has('graceDate')
                ? formatDaysUntilIndicator(planDetails.grace_days_until)
                : undefined,
            },
          ]
        : []),
      ...(planDetails.available_balance
        ? [
            {
              key: 'availableBalance',
              label: 'Available balance',
              value: planDetails.available_balance,
            },
          ]
        : []),
      ...(planDetails.rollover_date
        ? [
            {
              key: 'rolloverDate',
              label: 'Rollover date',
              value: formatDate(planDetails.rollover_date),
              indicator: nearestDeadlineKeys.has('rolloverDate')
                ? formatDaysUntilIndicator(planDetails.rollover_days_until)
                : undefined,
            },
          ]
        : []),
      ...(planDetails.election_max
        ? [
            {
              key: 'electionMax',
              label: 'Election max',
              value:
                planDetails.election_max.toUpperCase() === UNLIMITED_KEY
                  ? 'Unlimited'
                  : formatMonetaryValue(planDetails.election_max),
            },
          ]
        : []),
      ...(planDetails.rollover_date
        ? [
            {
              key: 'rolloverAmount',
              label: 'Rollover amount (min/max)',
              value: minMaxLabel,
            },
          ]
        : []),
      ...(planDetails.funding_method
        ? [
            {
              key: 'fundingMethod',
              label: 'Funding method',
              value: getFundingMethodLabel(planDetails.funding_method) || '',
            },
          ]
        : []),
      ...(planDetails.forfeiture_date
        ? [
            {
              key: 'forfeitureDate',
              label: 'Forfeiture date',
              value: formatDate(planDetails.forfeiture_date),
              indicator: nearestDeadlineKeys.has('forfeitureDate')
                ? formatDaysUntilIndicator(planDetails.forfeiture_days_until)
                : undefined,
            },
          ]
        : []),
      ...(planDetails.terminated_employees_runout
        ? [
            {
              key: 'terminatedEmployeesRunout',
              label: 'Terminated employees runout',
              value: planDetails.terminated_employees_runout,
            },
          ]
        : []),
    ];

    return rows;
  }, [minMaxLabel, nearestDeadlineKeys, planDetails]);

  return {
    isLoading,
    isError,
    isSuccess,
    refetch,
    data: dataFormatter,
  };
};

export default useGetNotionalPlanDetailsQuery;
